import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRightIcon, 
  TruckIcon, 
  CoinsIcon, 
  WheatIcon, 
  HeartHandshakeIcon,
  TargetIcon,
  EyeIcon,
  UsersIcon,
  LightbulbIcon,
  LeafIcon,
  CheckIcon
} from '../components/icons/Icons';
import { useSettings } from '../context/SettingsContext';
import './Home.css';

const Home = () => {
  const { settings } = useSettings();
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const services = [
    {
      icon: TruckIcon,
      title: 'Supply & Logistics',
      description: 'Enhancing value chains and ensuring efficient movement of goods across sectors.'
    },
    {
      icon: CoinsIcon,
      title: 'Finance',
      description: 'Providing innovative financial solutions and inclusive access to capital.'
    },
    {
      icon: WheatIcon,
      title: 'Agriculture',
      description: 'Investing in sustainable farming and agri-processing for food security.'
    },
    {
      icon: HeartHandshakeIcon,
      title: 'Partnerships',
      description: 'Strategic investment partnerships across diverse industries.'
    }
  ];

  const impactAreas = [
    { icon: UsersIcon, title: 'Economic Inclusion', description: 'Promoting job creation and economic opportunities' },
    { icon: LightbulbIcon, title: 'Innovation', description: 'Supporting local entrepreneurship and innovation' },
    { icon: LeafIcon, title: 'Sustainability', description: 'Championing environmental protection' }
  ];

  return (
    <>
      <Helmet>
        <title>Fuwali Investments Zambia Limited | Diversified Investment Holding Company</title>
        <meta name="description" content="Fuwali Investments Zambia Limited is a diversified investment holding company fostering innovative, impact-driven investment solutions in Supply & Logistics, Finance, Agriculture, and Strategic Partnerships." />
        <link rel="canonical" href="https://fuwalizambia.com/" />
        <meta property="og:title" content="Fuwali Investments Zambia Limited | Diversified Investment Holding Company" />
        <meta property="og:description" content="A diversified investment holding company fostering innovative, impact-driven investment solutions in Zambia." />
        <meta property="og:url" content="https://fuwalizambia.com/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero" style={settings.hero_bg_home ? { backgroundImage: `url(${settings.hero_bg_home})` } : {}}>
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <div className="hero-text">
            <span className="hero-badge">Diversified Investment Holding Company</span>
            <h1 className="hero-title">
              Fostering Innovative,<br />
              <span className="hero-highlight">Impact-Driven</span> Solutions
            </h1>
            <p className="hero-description">
              Our vision is to be a leading force in fostering innovative, impact-driven investment solutions that uplift communities and contribute to national progress.
            </p>
            <div className="hero-buttons">
              <Link to="/portfolio" className="btn btn-primary">
                Explore Our Portfolio <ArrowRightIcon size={18} />
              </Link>
              <Link to="/about" className="btn btn-outline">
                Learn More
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-card hero-card-1">
              <TargetIcon size={32} />
              <span>Strategic Growth</span>
            </div>
            <div className="hero-card hero-card-2">
              <EyeIcon size={32} />
              <span>Vision Driven</span>
            </div>
            <div className="hero-card hero-card-3">
              <HeartHandshakeIcon size={32} />
              <span>Community Impact</span>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="section about-preview">
        <div className="container">
          <div className="about-preview-grid">
            <div className="about-preview-content animate-on-scroll">
              <span className="section-label">About Us</span>
              <h2 className="section-title text-left">Building a Legacy of Impact</h2>
              <p className="about-preview-text">
               Fuwali Investments Zambia Limited was established with a bold vision: to build a diversified investment company that drives national development through innovation, job creation, and sustainable impact.
              </p>
              <p className="about-preview-text">
                Born out of a desire to empower communities and transform industries, Fuwali was created not only to invest, but to uplift.
              </p>
              <Link to="/about" className="btn btn-secondary">
                Our Full Story <ArrowRightIcon size={18} />
              </Link>
            </div>
            <div className="about-preview-stats animate-on-scroll">
              <div className="stat-card">
                <span className="stat-number">2024</span>
                <span className="stat-label">Year Founded</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">4+</span>
                <span className="stat-label">Core Sectors</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">100%</span>
                <span className="stat-label">Zambian Owned</span>
              </div>
              <div className="stat-card">
                <span className="stat-number">SDG</span>
                <span className="stat-label">Aligned Goals</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section services-section">
        <div className="container">
          <span className="section-label center">Our Portfolio</span>
          <h2 className="section-title">Strategic Business Focus</h2>
          <p className="section-subtitle">
            Our core focus spans key sectors that drive national development and economic resilience.
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card card animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="service-icon">
                  <service.icon size={32} />
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
              </div>
            ))}
          </div>
          <div className="services-cta">
            <Link to="/portfolio" className="btn btn-primary">
              View Full Portfolio <ArrowRightIcon size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="section impact-section">
        <div className="container">
          <div className="impact-grid">
            <div className="impact-content animate-on-scroll">
              <span className="section-label">Impact & Community</span>
              <h2 className="section-title text-left">Creating Lasting Change</h2>
              <p className="impact-text">
                We believe that true investment success goes beyond financial returns. It must create lasting, positive change in the lives of people and the environment we operate in.
              </p>
              <div className="impact-areas">
                {impactAreas.map((area, index) => (
                  <div key={index} className="impact-area">
                    <div className="impact-area-icon">
                      <area.icon size={24} />
                    </div>
                    <div>
                      <h4>{area.title}</h4>
                      <p>{area.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/impact" className="btn btn-primary">
                Learn About Our Impact <ArrowRightIcon size={18} />
              </Link>
            </div>
            <div className="impact-visual animate-on-scroll">
              <div className="impact-image-placeholder">
                <HeartHandshakeIcon size={80} />
                <span>Community First</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content animate-on-scroll">
            <h2>Ready to Partner With Us?</h2>
            <p>Join us in building a sustainable future for Zambia through strategic investments.</p>
            <Link to="/contact" className="btn btn-secondary">
              Get In Touch <ArrowRightIcon size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
